import {useConfig} from '@aboutyou/config';
import React from 'react';
import styled, {css} from 'styled-components';
import {COLORS} from '../../../../../constants/colors';
import {mobileOnly, tabletAndUp} from '../../../../../constants/customMedia';
import {SPACING} from '../../../../../constants/sizing';
import {Checkbox, InputWrapper} from '../../../../atoms/Checkbox/Checkbox';
import {NumberBadge} from '../../../../atoms/NumberBadge/NumberBadge';
import {RadioButton} from '../../../../atoms/RadioButton/RadioButton';
import {Text} from '../../../../atoms/Text';

export type AttributeFilterItem = {
  count?: number;
  id: string;
  label: string;
  slug?: string;
  description?: string;
};

interface Props {
  activeItemIds: Array<string>;
  dataTestId?: string;
  items: Array<AttributeFilterItem>;
  onAttributeClick: (itemId: string, itemSlug?: string) => void;
  isSingleSelect?: boolean;
  className?: string;
  enableFilterFlyout?: boolean;
}

export const AttributeItems: React.FC<Props> = ({
  items,
  activeItemIds,
  onAttributeClick,
  isSingleSelect,
  dataTestId,
  className,
  enableFilterFlyout,
}) => {
  const {device} = useConfig();
  const showSeparator = items.some(item => item.description);

  const AttributeItem = isSingleSelect ? RadioButton : StyledCheckbox;

  return (
    <Wrapper
      data-testid={dataTestId}
      className={className}
      data-show-item-separator={showSeparator}
      data-enable-filter-flyout={enableFilterFlyout}
    >
      {items.map(item => {
        const {count, id, label, description, slug} = item;
        const active = activeItemIds.includes(`${id}`);
        const key = `filter-generic-items-${
          isSingleSelect ? 'radio' : 'checkbox'
        }-${item.id}`;

        return (
          <AttributeItemWrapper
            key={key}
            $withoutMarginBottom={enableFilterFlyout || showSeparator}
          >
            <AttributeItem
              data-testid={key}
              $largerMarginLabel={enableFilterFlyout}
              checked={active}
              fullLabelWidth={device === 'mobile'}
              label={
                <LabelWrapper>
                  <Text>{label}</Text>
                  {count !== undefined && count > 0 && (
                    <Count
                      active={active}
                      count={count}
                      size="small"
                      $largerMargin={enableFilterFlyout}
                    />
                  )}
                </LabelWrapper>
              }
              subLabel={
                description ? (
                  <Description
                    color="Grey1"
                    size="px12"
                    $largerMargin={enableFilterFlyout}
                  >
                    {description}
                  </Description>
                ) : null
              }
              onChange={() => {
                onAttributeClick(item.id, slug);
              }}
            />
          </AttributeItemWrapper>
        );
      })}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  padding: 16px 24px;
  background-color: ${COLORS.White};

  ${mobileOnly(css`
    padding: 0 0 ${SPACING['Y.Lg']} 0;

    &[data-show-item-separator='false'][data-enable-filter-flyout='true'] {
      padding: unset;
      display: flex;
      flex-direction: column;
      gap: 24px;
      padding-bottom: 12px;
    }

    &[data-enable-filter-flyout='true'] {
      padding: 0;
    }
  `)}
`;

const LabelWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const AttributeItemWrapper = styled.div<{$withoutMarginBottom?: boolean}>`
  display: flex;
  flex-direction: column;
  width: 100%;

  &:not(:last-child) {
    margin-bottom: ${props => (props.$withoutMarginBottom ? '0' : '1.125rem')};
  }

  [data-show-item-separator='true'] & {
    &:not(:last-child):after {
      --spacing: calc(20px + 15px); // checkbox width + checkbox margin

      content: '';
      display: block;
      width: 100%;
      height: 1px;
      background: ${COLORS.Grey4};
      margin-block: 1rem;
    }
  }
`;

const Count = styled(NumberBadge)<{
  $largerMargin?: boolean;
}>`
  color: ${COLORS.Black};
  flex-shrink: 0;
  margin: ${props => (props.$largerMargin ? '1px 0 0 1rem' : '1px 0 0 0.5rem')};
  line-height: 1;
`;

const StyledCheckbox = styled(Checkbox)<{
  $largerMarginLabel?: boolean;
}>`
  color: ${COLORS.Black};

  &[data-full-width='true'] {
    ${InputWrapper} {
      width: 100%;
    }
  }

  & div {
    margin-right: ${props => (props.$largerMarginLabel ? '1.25rem' : '')};
  }
`;

const Description = styled(Text)<{
  $largerMargin?: boolean;
}>`
  display: block;
  line-height: 1.5;
  margin-top: 4px;

  // checkbox width + checkbox margin
  padding-left: ${props =>
    props.$largerMargin ? 'calc(20px + 20px)' : 'calc(20px + 15px)'};

  ${tabletAndUp(css`
    padding-left: calc(20px + 15px); // checkbox width + checkbox margin
  `)}
`;
